import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, RotateCcw, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { FormFields } from "./fields";
import { SETTINGS_TABS } from "./schemas";
import HomeSections from "./HomeSections";
import ResourcesAdmin from "./Resources";
import { CategoriesAdmin, PagesAdmin, SubscribersAdmin, UsersAdmin } from "./Collections";
import WeeklyIssues from "./WeeklyIssues";

const CONTENT_TABS = [
  { id: "resources", label: "Resources", C: ResourcesAdmin },
  { id: "categories", label: "Categories", C: CategoriesAdmin },
  { id: "pages", label: "Pages", C: PagesAdmin },
  { id: "subscribers", label: "Subscribers", C: SubscribersAdmin },
  { id: "users", label: "Members", C: UsersAdmin },
];

function SettingsEditor({ tab }) {
  const { reload } = useSite();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setDraft(r.data));
  }, []);
  if (!draft) return <p>Loading...</p>;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", draft);
      await reload();
      toast.success("Saved. Your changes are live.");
    } catch (e) {
      toast.error(formatErr(e));
    }
    setSaving(false);
  };
  const reset = async () => {
    if (!window.confirm("Reset ALL site settings to the original defaults?")) return;
    const { data } = await api.post("/admin/settings/reset");
    setDraft(data);
    await reload();
    toast.success("Settings reset");
  };
  return (
    <div>
      <div className="sticky top-[72px] z-10 -mx-1 mb-6 flex items-center justify-between gap-3 bg-bt-bg px-1 py-3">
        <h2 className="text-2xl">{tab.label}</h2>
        <div className="flex gap-2">
          {tab.id === "brand" && <button onClick={reset} className="btn-ghost text-sm" data-testid="admin-settings-reset"><RotateCcw size={15} /> Reset</button>}
          <button onClick={save} disabled={saving} className="btn-primary !min-h-[44px]" data-testid="admin-cms-save-settings">{saving ? "Saving..." : "Save changes"}</button>
        </div>
      </div>
      {tab.custom === "home" ? <HomeSections draft={draft} setDraft={setDraft} /> : <FormFields schema={tab.fields} value={draft} onChange={setDraft} prefix={`settings-${tab.id}`} />}
      {tab.custom === "weekly" && <WeeklyIssues />}
    </div>
  );
}

function TestMemberButton() {
  const { testMemberLogin } = useAuth();
  const nav = useNavigate();
  const go = async () => {
    try {
      await testMemberLogin();
      toast.success("Signed in as the test member. Sign out and back in as admin when you're done.");
      nav("/account");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <button onClick={go} className="btn-ghost text-sm" title="Only visible to admins" data-testid="admin-test-member-login">
      <UserRound size={15} /> Log in as test member
    </button>
  );
}

export default function Admin() {
  const [active, setActive] = useState("resources");
  const all = [...CONTENT_TABS, ...SETTINGS_TABS];
  const tab = all.find((t) => t.id === active);
  const Btn = ({ t }) => (
    <button onClick={() => setActive(t.id)} data-testid={`admin-tab-${t.id}`}
      className={`shrink-0 rounded-xl px-4 py-2.5 text-left text-sm font-medium transition-colors ${active === t.id ? "bg-bt-primary text-bt-bg" : "text-bt-heading hover:bg-bt-sage"}`}>{t.label}</button>
  );
  return (
    <div className="container-bt py-10" data-testid="admin-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <p className="eyebrow mb-1">Admin</p>
          <h1 className="text-3xl sm:text-4xl">Content & settings</h1>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <TestMemberButton />
          <Link to="/" target="_blank" className="btn-ghost text-sm" data-testid="admin-view-site"><ExternalLink size={15} /> View site</Link>
        </div>
      </div>
      <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
        <aside className="-mx-5 flex gap-1 overflow-x-auto px-5 pb-2 lg:sticky lg:top-24 lg:mx-0 lg:flex-col lg:self-start lg:overflow-visible lg:px-0">
          <p className="hidden px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-widest text-bt-sagedeep lg:block">Content</p>
          {CONTENT_TABS.map((t) => <Btn key={t.id} t={t} />)}
          <p className="hidden px-4 pb-1 pt-5 text-xs font-semibold uppercase tracking-widest text-bt-sagedeep lg:block">Site</p>
          {SETTINGS_TABS.map((t) => <Btn key={t.id} t={t} />)}
        </aside>
        <section className="min-w-0">
          {tab.C ? <tab.C /> : <SettingsEditor key={tab.id} tab={tab} />}
        </section>
      </div>
    </div>
  );
}
